import {
    currentUserAppPreferencesVersion,
    UserAppPreferencesModel,
    UserAppPreferencesVersion,
} from '../models/user-app-preferences.model';
import { fromNoVersionToOnePointOh } from './user-app-preference-versions/1.0';
import { fromOnePointOhToOnePointOne } from './user-app-preference-versions/1.1';
import { fromOnePointOneToOnePointTwo } from './user-app-preference-versions/1.2';
import { fromOnePointTwoToOnePointThree } from './user-app-preference-versions/1.3';
import { fromOnePointThreeToOnePointFour } from './user-app-preference-versions/1.4';
import { fromOnePointFourToOnePointFive } from './user-app-preference-versions/1.5';
import { fromOnePointFiveToOnePointSix } from './user-app-preference-versions/1.6';
import { fromOnePointFiveToOnePointSeven } from './user-app-preference-versions/1.7';

type TransformationFunction = (oldDataModel: UserAppPreferencesModel) => UserAppPreferencesModel;

/**
 * Handles the transformations for versioning UserAppPreferences JSON objects
 *
 * Any breaking / non-backwards compatible changes (deletions, breaking updates) should be accompanied by a version change.
 * Adding fields or options to a field are non-breaking and do not require a version change.
 * Version changes should be encapsulated by a transformation function, which is then mapped to the previous version.
 * e.g.,
 *      Current Version {@link currentUserAppPreferencesVersion}
 *      Some breaking Change is added
 *      '1.2' added to {@link UserAppPreferencesVersion} union
 *      {@link currentUserAppPreferencesVersion} updated to '1.2'
 *      Transformation function 'fromOnePointTwoToOnePointThree' is created, which updates breaking data to new format
 *      A new entry in the {@link transformationJumpTable} is added:
 *          - ['1.2']: this.fromOnePointTwoToOnePointThree,
 */
export class UserAppPreferencesVersioner {
    private readonly transformationJumpTable: { [k in UserAppPreferencesVersion]?: TransformationFunction } = {
        ['0.0']: fromNoVersionToOnePointOh,
        ['1.0']: fromOnePointOhToOnePointOne,
        ['1.1']: fromOnePointOneToOnePointTwo,
        ['1.2']: fromOnePointTwoToOnePointThree,
        ['1.3']: fromOnePointThreeToOnePointFour,
        ['1.4']: fromOnePointFourToOnePointFive,
        ['1.5']: fromOnePointFiveToOnePointSix,
        ['1.6']: fromOnePointFiveToOnePointSeven
    };

    transformDataToNewestVersion(oldDataModel: UserAppPreferencesModel): UserAppPreferencesModel {
        let currentData: UserAppPreferencesModel = oldDataModel;
        while (this.versionAsNumber(currentData) < this.versionAsNumber(currentUserAppPreferencesVersion)) {
            currentData = this.transformationJumpTable[this.getVersion(currentData)](currentData);
        }
        return currentData;
    }

    private versionAsNumber(modelOrVersion: UserAppPreferencesModel | UserAppPreferencesVersion): number {
        if (typeof modelOrVersion === 'object') {
            const version = this.getVersion(modelOrVersion);
            return Number(version);
        }

        return Number(modelOrVersion);
    }

    private getVersion(model: UserAppPreferencesModel): UserAppPreferencesVersion {
        return model?.version ?? '0.0';
    }
}
